import { PMI } from '@pvw/plmvisweb';
import { SELECTION_SCOPE, getDefaultSelectionScope, isDimension } from './atlasSelectionUtils';
import BasicElements, { SelectionElement, SelectionObject } from './core-elements/BasicElements';

// Extract the meat of this.findObjects for reuse
/**
 *
 * @param {string} psId - psId
 * @param {SelectionElement[]} elements - Selection elements
 * @param {Map<string, SelectionObject>} selected - selected map
 * @returns {Promise<SelectionObject[]>}
 */
const findUnselectedObjects = async (psId, elements, selected) => {
    let objPromiseArr = elements.filter((el) => el.active).map((el) => ({ ofObj: el.ofObject(psId), elem: el }));
    let result = (
        await Promise.all(
            objPromiseArr.map(async (objPromise) => {
                let obj = await objPromise.ofObj;
                if (obj) {
                    // isSelected isn't perfect, as the psId may
                    // be for a edge, but is recognized as a part or body.
                    // so isReallySelected determines if it's really selected here.
                    if (Array.isArray(obj)) {
                        return obj.map((mem) => {
                            if (mem) {
                                let isReallySelected = selected.get(JSON.stringify(mem.id));
                                if (!isReallySelected) {
                                    mem.sourceElement = objPromise.elem;
                                    return mem;
                                }
                            }
                        });
                    }
                    let isReallySelected = selected.get(JSON.stringify(obj.id));
                    if (!isReallySelected) {
                        obj.sourceElement = objPromise.elem;
                        return obj;
                    }
                }
            })
        )
    )
        .flat()
        .filter((obj) => obj);

    return result;
};

/**
 * @class
 * @param {object} viewer - An instance of PLMVisWeb viewer
 * @param {SelectionElement[]} [filterElements] - selection elements array
 */
export function Context(viewer, filterElements) {
    /** @type {SelectionElement[]} */
    let elements = [];
    let _multiSelect = true;
    let _secondaryActive = false; // default is turning off secondary selection display
    /** @type {Map<string, SelectionObject>} */
    let selected = new Map();
    let _selectionScope = getDefaultSelectionScope(); // undefined is the same as entire assembly
    // We need to figure out how to handle the snap point secondary selection display. I'll avoid try to do anything
    // in this submission. Use snapPointTypes as a temporary measure.
    let snapPointTypes = new Set(['SnapPoint', 'SnapOnFace', 'SnapOnCurve']);
    let allowAreaSelect = true;
    let contextName = 'default';
    let dragAndDropFlag = false;

    this.addToFilter = (filterElements) => {
        Array.prototype.push.apply(elements, filterElements);
        for (let el of filterElements) {
            if (!(el instanceof BasicElements.SelectionElement)) {
                throw new Error('Selection Elements must inherit from BasicElements.SelectionElement');
            }
            el.active = true;
        }
    };

    if (filterElements) {
        this.addToFilter(filterElements);
    }

    /**
     *
     * @returns {SelectionElement[]}
     */
    this.getElements = () => {
        //returns a copy, internal data can't be edited directly
        //To edit use addToFilter and clearFilter
        return elements.slice(0);
    };

    this.onPop = () => {
        for (let el of elements) {
            if (el.onPop) {
                el.onPop();
            }
        }
    };

    /**
     *
     * @param {SelectionElement[]} filterElements - selection elements
     * @param {boolean} active - is element active
     * @param {boolean} exclusive
     * @param {boolean} [dragAndDrop]
     */
    this.setActive = (filterElements, active, exclusive, dragAndDrop = false) => {
        if (exclusive) {
            for (let el of elements) {
                el.active = !active;
            }
        }
        for (let el of filterElements) {
            el.active = active;
        }

        dragAndDropFlag = dragAndDrop;
    };

    /**
     * Deactive selection elements
     *
     * @param {SelectionElement[]} inputElements - Selection elements
     */
    this.setDeactive = (inputElements) => {
        elements.forEach((elem) => {
            inputElements.forEach((inputElem) => {
                // if (elem.getDisplayName() === elemName) {
                if (elem.constructor === inputElem) {
                    elem.active = false;
                }
            });
        });
    };

    this.isDragAndDrop = () => {
        return dragAndDropFlag;
    };

    /**
     *
     * @param {boolean} [areaSelect=false]
     * @returns {number} - picking mask
     */
    this.getPickingMask = (areaSelect = false) => {
        let pickMask = 0;
        for (let el of filterElements) {
            if (el.active) {
                pickMask |= el.getPickingMask(areaSelect);
            }
        }
        return pickMask;
    };

    this.clearFilter = () => {
        elements.length = 0;
    };
    /** @param {SelectionObject[]} objects */
    this.addToSelected = (objects) => {
        for (let obj of objects) {
            obj.sourceElement.onAddToSelected(this, obj);
            selected.set(JSON.stringify(obj.id), obj);
        }
    };
    /** @param {SelectionObject[]} objects */
    this.removeFromSelected = (objects) => {
        for (let obj of objects) {
            obj.sourceElement.onRemoveFromSelected(this, obj);
            selected.delete(JSON.stringify(obj.id));
        }
    };
    /** @param {SelectionObject} selObj */
    this.isObjSelected = (selObj) => {
        return this.isAtlasObjSelected(selObj.id);
    };
    /** @param {string | any[]} atlasId - atlasId of an object*/
    this.isAtlasObjSelected = (atlasId) => {
        return selected.has(JSON.stringify(atlasId));
    };
    /**
     *
     * @param {string} psId - PlmVisWeb id
     * @returns {boolean} - is selected or not
     */
    this.isSelected = (psId) => {
        // let atlasId = undefined;
        // if ( isDimension( psId ) ) {
        //     // for dimension, selObj.id is the same as dimension id which is the same as "psId"
        //     atlasId = psId;
        // } else {
        //     atlasId = ConvertIds.fromPsId(viewer, psId);
        // }
        // return selected.has(JSON.stringify(atlasId));

        if (isDimension(psId)) {
            let dimManager = viewer.dimension;
            let ret = dimManager.getSelectionByDimId(psId);
            return ret;
        }

        let ret = viewer.getSelectionByPsId(psId);
        if (ret === undefined) {
            if (PMI && viewer.pmi) {
                var pmiMgr = viewer.pmi;
                if (pmiMgr) {
                    ret = pmiMgr.getSelectionByPsId(psId);
                }
            }
        }
        return ret;
    };
    /** @returns {number} */
    this.numSelected = () => {
        return selected.size;
    };
    this.clearSelected = () => {
        for (let obj of this.getSelected()) {
            obj.sourceElement.onRemoveFromSelected(this, obj);
        }
        selected.clear();
    };
    /** @returns {IterableIterator<SelectionObject>} */
    this.getSelected = () => selected.values();
    this[Symbol.iterator] = () => selected.values();

    /**
     * for given psId, find all objects in order of prioritized filter list
     *
     * @param {string} psId - PlmVisWeb id
     * @param {boolean} [deselect=false] - is deselect
     * @returns {Promise<SelectionObject[]>} - Selection objects satisfied
     */
    this.findObjects = async (psId, deselect = false) => {
        /** @type {SelectionObject[]} */
        let result = [];
        let isSel = this.isSelected(psId);
        if (deselect && isSel) {
            // check all elements for an object.  An inactive
            // element could have members on the selection list
            // (Galaxy navigator selection)
            let objPromiseArr = elements.map((el) => ({ ofObj: el.ofObject(psId), elem: el }));

            result = (
                await Promise.all(
                    objPromiseArr.map(async (objPromise) => {
                        let obj = await objPromise.ofObj;
                        if (obj) {
                            // isSelected isn't perfect, as the psId may
                            // be for a edge, but is recognized as a part or body.
                            // so isReallySelected determines if it's really selected here.
                            if (Array.isArray(obj)) {
                                return obj.map((mem) => {
                                    if (mem) {
                                        let isReallySelected = selected.get(JSON.stringify(mem.id));
                                        if (isReallySelected) {
                                            mem.sourceElement = objPromise.elem;
                                            return mem;
                                        }
                                    }
                                });
                            }
                            let isReallySelected = selected.get(JSON.stringify(obj.id));
                            if (isReallySelected) {
                                obj.sourceElement = objPromise.elem;
                                return obj;
                            }
                        }
                    })
                )
            )
                .flat()
                .filter((obj) => obj);
        } else if (!deselect && !isSel) {
            return findUnselectedObjects(psId, elements, selected);
        } else if (isSel) {
            // We need to consider the case where the edge, face, or curve is selected and the objects derived from
            // them are not. For example, SnapPoint, SnapOnFace, or SnapOnCurve are not selected but the edge, face,
            // or curve is selected.
            return findUnselectedObjects(psId, elements, selected);
        }
        return result;
    };
    /**
     *
     * @param {boolean} multiSelect - is multi select
     */
    this.setMultiSelect = (multiSelect) => {
        _multiSelect = multiSelect;
        this.setAllowAreaSelect(_multiSelect);
    };
    /**
     *
     * @returns {boolean}
     */
    this.getMultiSelect = () => {
        return _multiSelect;
    };

    /**
     *  return the psIds involved in the selected objects.
     *  For faces, edges, etc., only face or edge psId is returned; for features, the psIds of the array of faces involved in the
     *  feature are returned.
     *
     *  @returns {Set<string>} psIds involved in the selected objects.
     */
    this.getPsIdsInSelected = () => {
        let selectedObjs = Array.from(this.getSelected());
        let psIdSet = new Set();
        for (const selObj of selectedObjs) {
            // skip all snap points for now. we'll handle that later
            if (!snapPointTypes.has(selObj.type)) {
                selObj.psIds.forEach((item) => psIdSet.add(item));
            }
        }
        return psIdSet;
    };

    /**
     * set secondary selection display based on the input
     *
     * @param {boolean} secondaryActive - true to turn on and false to turn off the secondary selection display
     */
    this.setSecondaryActive = (secondaryActive) => {
        _secondaryActive = secondaryActive;
    };
    this.getSecondaryActive = () => {
        return _secondaryActive;
    };
    /** @param {boolean} areaSelect */
    this.setAllowAreaSelect = (areaSelect) => {
        if (areaSelect && !this.getMultiSelect()) {
            return;
        }
        allowAreaSelect = areaSelect;
    };
    this.getAllowAreaSelect = () => {
        return allowAreaSelect;
    };
    /**
     *
     * @param {string} name
     */
    this.setName = (name) => {
        if (contextName !== name) {
            contextName = name;
        }
    };
    this.getName = () => {
        return contextName;
    };
    /**
     *
     * @param {string} name - display name of element
     * @returns {boolean}
     */
    this.hasActiveElement = (name) => {
        const elems = this.getElements();
        const dimElem = elems.find((elem) => elem.getDisplayName() === name);
        return dimElem ? Boolean(dimElem.active) : false;
    };
    this.setSelectionScope = (selectionScope) => {
        const validScope =
            selectionScope === SELECTION_SCOPE.EntireAssembly ||
            selectionScope === SELECTION_SCOPE.WithinWorkPartAndComponents ||
            selectionScope === SELECTION_SCOPE.WithinWorkPartOnly;
        if (validScope) {
            _selectionScope = selectionScope;
        }
    };
    this.getSelectionScope = () => {
        return _selectionScope;
    };
}
export default Context;
